'use client';

/**
 * Gesture-driven permission prompt. Render this inside settings or a CTA card.
 * Renders nothing if push isn't supported or permission already granted/denied
 * (callers should drive UX from `currentPermission()` if they want copy).
 */

import { useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { toast } from 'sonner';
import {
  checkPermission,
  getFcmToken,
  getPlatformKind,
  isPushSupported,
  requestNotificationPermission,
} from '@/lib/firebase/messaging';
import { registerDevice } from '@/lib/notifications-client';
import { getInstallationId } from '@/lib/installation-id';

export function NotificationPermissionPrompt({
  className,
  title = 'Enable notifications',
  description = 'Get real-time updates about your requests, payments and more.',
  ctaLabel = 'Enable',
}: {
  className?: string;
  title?: string;
  description?: string;
  ctaLabel?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [perm, setPerm] = useState<NotificationPermission | 'unsupported'>('default');

  useEffect(() => {
    setMounted(true);
    let cancelled = false;
    // Read the *real* OS permission asynchronously — on native the sync proxy
    // is wrong until a token is registered, which made this prompt show the
    // "Enable" button every time even after notifications were enabled.
    void checkPermission().then((p) => {
      if (!cancelled) setPerm(p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!mounted) return null;
  if (!isPushSupported()) return null;
  if (perm === 'granted' || perm === 'denied' || perm === 'unsupported') return null;

  const handleEnable = async () => {
    setBusy(true);
    try {
      const result = await requestNotificationPermission();
      setPerm(result);
      if (result !== 'granted') {
        toast.message('Notifications not enabled', {
          description: 'You can enable them later from Settings.',
        });
        return;
      }
      const token = await getFcmToken();
      if (!token) {
        toast.error('Could not register device for notifications.');
        return;
      }
      await registerDevice({
        token,
        platform: getPlatformKind(),
        locale: typeof navigator !== 'undefined' ? navigator.language : undefined,
        timezone:
          typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
        installationId: getInstallationId(),
      });
      toast.success('Notifications enabled.');
    } catch (e) {
      console.error('[NotificationPermissionPrompt] failed', e);
      toast.error('Could not enable notifications.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={className ?? 'flex items-start gap-3 rounded-lg border p-4 bg-muted/30'}>
      <Bell className="w-5 h-5 mt-0.5 text-primary" />
      <div className="flex-1">
        <p className="text-sm font-medium">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
      <button
        type="button"
        onClick={handleEnable}
        disabled={busy}
        className="px-3 py-1.5 text-xs font-medium rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
      >
        {busy ? '…' : ctaLabel}
      </button>
    </div>
  );
}
